import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import validateForm from "../../../utils/FormValidation";
import validationRules from "../../../assets/validation/rules";
import AuthRestAPI from "../../../restAPI/AuthRestAPI";
import moguLogo from "../../../assets/logo/mogu-logo-cover.png";
import AuthPageAside from "./AuthPageAside";
import "./AuthFlow.css";

const Brand = () => (
  <div className="brand">
    <span className="brand-mark brand-mark--login-cover">
      <img className="brand-mark-image-cover" src={moguLogo} alt="MOGU logo" />
    </span>
  </div>
);

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { token } = useSelector((state) => state.auth);
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const validateFormData = (data) => {
    const validationResult = validateForm(data, validationRules);
    if (!validationResult.isValid) {
      const err = validationResult.errors;
      const newErrors = {};
      Object.keys(err).forEach((value) => {
        newErrors[value] = err[value];
      });
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return false;
    }
    const keyData = Object.keys(data)[0];
    setErrors((prev) => ({ ...prev, [keyData]: "" }));
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const normalizedEmail = email.trim();
    if (!validateFormData({ forgotPasswordEmail: normalizedEmail })) return;

    setSubmitting(true);
    setSubmitError("");
    try {
      await AuthRestAPI.forgotPassword(
        token,
        localStorage.getItem("groupId"),
        localStorage.getItem("companyId"),
        { email: normalizedEmail }
      );
      navigate(`/forgot-password-sent?email=${encodeURIComponent(normalizedEmail)}`);
    } catch (err) {
      setSubmitError(err?.response?.data?.message || "Unable to send reset link. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="shell">
      <section className="left">
        <Brand />
        <div className="form-wrap">
          <button type="button" className="back link-button" onClick={() => navigate("/login")}>Back to sign in</button>
          <div className="accent-bar"></div>
          <h1>Forgot your <em>password?</em></h1>
          <p className="subtitle">Enter the email associated with your MOGU account and we’ll send a reset link.</p>

          {submitError && <div className="field-error" role="alert">{submitError}</div>}

          <form onSubmit={handleSubmit}>
            <div className="field" style={{ marginBottom: 24 }}>
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                placeholder="name@yourcompany.com"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  const v = e.target.value;
                  validateFormData({ forgotPasswordEmail: v });
                  setEmail(v);
                }}
                required
              />
              {errors.forgotPasswordEmail && <span className="field-error">{errors.forgotPasswordEmail}</span>}
            </div>
            <button className="btn" type="submit" disabled={submitting}>
              {submitting ? "Sending..." : "Send reset link"}
            </button>
          </form>

          <p className="secondary-link">
            Remembered your password? <button type="button" className="link-button" onClick={() => navigate("/login")}>Sign in</button>
          </p>
          <div className="foot"><span>© {new Date().getFullYear()} MOGU Sdn Bhd</span></div>
        </div>
      </section>

      <AuthPageAside>
        <div className="right-eyebrow">Password recovery</div>
        <h2>Get back into your <em>account</em> in three steps.</h2>
        <p className="lede">We’ll email you a secure reset link that expires in 30 minutes.</p>
        <div className="panel">
          <div className="panel-title" style={{ marginBottom: 18 }}>How it works</div>
          <ol className="step-list">
            <li><div><strong>Enter your email</strong><span>The same email used for MOGU sign in.</span></div></li>
            <li><div><strong>Check your inbox</strong><span>A secure reset link will arrive shortly.</span></div></li>
            <li><div><strong>Create a new password</strong><span>Use the link to set a new password.</span></div></li>
          </ol>
        </div>
      </AuthPageAside>
    </div>
  );
};

export default ForgotPassword;
